import { useEffect, useState } from "react";
import { UserPlus, Users, UserX } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { errMsg } from "./kpiFormat";
import { PackAssignDialog } from "./PackAssignDialog";

const STATUS = {
  unmatched: { label: "No customer", cls: "bg-red-50 text-red-700 border-red-200" },
  booking: { label: "From bookings", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  manual: { label: "Assigned by you", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-");

const SharedNote = ({ r }) => !r.shared ? null : (
  <div className="mt-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-2xs text-amber-900" data-testid={`kpi-owner-shared-${slug(r.id)}`}>
    <div className="flex items-center gap-1 font-semibold"><Users className="h-3 w-3" />Shared by {r.booking_clients.length} clients — each counts as a member</div>
    <div>{r.booking_clients.map((b) => `${b.name || b.client_id} (${b.uses})`).join(" · ")}</div>
  </div>
);

const OwnerRow = ({ r, canEdit, onAssign, onRemove }) => {
  const st = STATUS[r.status] || { label: "Acuity email", cls: "bg-zinc-50 text-zinc-600 border-zinc-200" };
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { if (!confirm) return undefined; const t = setTimeout(() => setConfirm(false), 4000); return () => clearTimeout(t); }, [confirm]);
  return (
    <tr className={cn("border-t border-zinc-100 align-top", !r.live && "text-zinc-400")} data-testid={`kpi-owner-row-${slug(r.id)}`}>
      <td className="py-2 pr-3"><div className="font-medium text-zinc-900">{r.product}</div><div className="font-mono-num text-2xs text-zinc-500">{r.code || r.id}</div></td>
      <td className="py-2 pr-3 font-mono-num text-xs whitespace-nowrap">{r.start_at ? fmtDate(r.start_at) : "—"}</td>
      <td className="py-2 pr-3 font-mono-num text-xs whitespace-nowrap">{r.expires_at ? fmtDate(r.expires_at) : "—"}{!r.live && " (ended)"}</td>
      <td className="py-2 pr-3 font-mono-num text-xs">{r.credits_remaining ?? "—"}</td>
      <td className="py-2 pr-3 max-w-sm">
        <span className={cn("inline-block rounded-sm border px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-[0.1em]", st.cls)}
          data-testid={`kpi-owner-status-${slug(r.id)}`}>{st.label}</span>
        {r.client_id && <div className="mt-1 text-sm text-zinc-900">{r.client_name || r.client_email}
          {r.client_name && <span className="ml-1 text-2xs text-zinc-500">{r.client_email}</span>}</div>}
        {r.status === "manual" && <div className="text-2xs text-zinc-500">by {r.assigned_by} · {fmtDate(r.assigned_at)}{r.note ? ` · ${r.note}` : ""}</div>}
        <SharedNote r={r} />
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        {canEdit && (
          <div className="flex justify-end gap-1.5">
            {STATUS[r.status] && (
              <Button size="sm" variant={r.status === "unmatched" ? "default" : "outline"} onClick={() => onAssign(r)} data-testid={`kpi-owner-assign-${slug(r.id)}`}>
                <UserPlus className="h-3.5 w-3.5 mr-1" />{r.status === "unmatched" ? "Assign" : "Change"}
              </Button>
            )}
            {r.status === "manual" && (
              <Button size="sm" variant={confirm ? "destructive" : "ghost"} data-testid={`kpi-owner-remove-${slug(r.id)}`}
                onClick={() => (confirm ? onRemove(r) : setConfirm(true))} aria-label="Remove assignment">
                <UserX className="h-3.5 w-3.5" />{confirm && <span className="ml-1">Confirm remove</span>}
              </Button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

export const UnmatchedPacks = ({ canEdit }) => {
  const [data, setData] = useState(null);
  const [all, setAll] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/kpi/entitlements/owners", { params: { show_all: all } }).then((r) => setData(r.data))
    .catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [all]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (r) => {
    try {
      await api.delete(`/kpi/entitlements/${encodeURIComponent(r.id)}/owner`);
      toast.success("Assignment removed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const c = data?.counts || {};
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-6 space-y-4" data-testid="kpi-unmatched-packs">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold">Pack holders</h3>
          <p className="text-sm text-zinc-500 max-w-2xl">Packs with no customer in Acuity go to the client who booked with their code; any left over
            aren't counted as members until you assign them. Your assignment always wins, even after later syncs. When a pack is used by more than one client (e.g. family), everyone who
            books with it counts as an active member. Refresh the week's draft afterwards.</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-600">
          <Switch checked={all} onCheckedChange={setAll} data-testid="kpi-owner-show-all" />Show ended packs and Acuity-matched ones
        </label>
      </div>
      <div className="flex flex-wrap gap-2 text-xs" data-testid="kpi-owner-counts">
        {Object.entries(STATUS).map(([k, s]) => (
          <span key={k} className={cn("rounded-full border px-2.5 py-0.5", s.cls)} data-testid={`kpi-owner-count-${k}`}>{s.label}: {c[k] ?? 0} live</span>
        ))}
        <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-amber-900" data-testid="kpi-owner-count-shared">
          Shared: {c.shared ?? 0} live</span>
      </div>
      {!data ? <div className="h-20 animate-pulse rounded bg-zinc-100" /> : data.rows.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="kpi-owner-empty">Every live pack has a customer and none are shared.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-2xs uppercase tracking-[0.18em] text-zinc-500">
              <th className="pb-2">Pack</th><th className="pb-2">Started</th><th className="pb-2">Expires</th><th className="pb-2">Credits left</th>
              <th className="pb-2">Customer</th><th /></tr></thead>
            <tbody>{data.rows.map((r) => <OwnerRow key={r.id} r={r} canEdit={canEdit} onAssign={setEditing} onRemove={remove} />)}</tbody>
          </table>
        </div>
      )}
      {editing && <PackAssignDialog row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </section>
  );
};
